import { IndexableType, PromiseExtended } from 'dexie';
import { projectInDtoToEntity } from 'shared/domain/project/mapping/toEntity';
import { ProjectEntity } from 'shared/domain/project/types/entity';
import { ProjectInDto } from 'shared/domain/project/types/dto';
import { Changes } from 'shared/types/commonEntities';
import { LogLevel } from 'shared/types/logger';
import {
  createEntityMapById,
  idsFromEntities,
} from 'shared/utils/id/entityIds';
import {
  makeDefaultGetMany,
  makeDefaultGetOne,
  makeDefaultRemoveBatch,
  makeReplaceAllBatchMapped,
} from '../defaultDaoFactories';
import { getErrorMsg, logWithRethrow, Operation } from '../helpers';
import db from '../index';
import { wrapQuery } from '../queryWrapper';
import { entityDbClearFactory } from '../entityDbClearFactory';
import { clear as projectsServiceClear } from './projectsService';
import { makeClearPublisher } from 'serviceWorker/broadcasts/publishers';
import { projectContract } from 'shared/domain/project/projectContract';

/** The rows with the download state: the state left over an empty table would keep the collection "downloaded". */
export const clear = entityDbClearFactory(
  db,
  ['projects', 'projectsService'],
  () => db.projects.clear(),
  projectsServiceClear,
  makeClearPublisher(projectContract)
);

export const getOne = makeDefaultGetOne<string, ProjectEntity>(
  db,
  db.projects,
  '_id'
);

export const get = makeDefaultGetMany<ProjectEntity>(db, db.projects);

/** The downloaded list replaces the stored one: a project the user lost access to is dropped. */
export const addBatch = makeReplaceAllBatchMapped<ProjectInDto, ProjectEntity>(db, db.projects, projectInDtoToEntity);

export const updateBatch = addBatch;

export const updateLocalBatch = wrapQuery(
  db,
  async (data: ProjectInDto[]): Promise<IndexableType> => {
    return db.transaction('rw', db.projects, async () => {
      const existingProjects = await db.projects.bulkGet(idsFromEntities(data));
      const existingById = createEntityMapById(
        existingProjects.filter((p): p is ProjectEntity => p !== undefined)
      );

      return db.projects.bulkPut(
        data.map((project) =>
          projectInDtoToEntity(project, existingById.get(project._id))
        )
      );
    }).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.addBatch, 'projects'),
        errorObj: e,
        additionalInfo: { query: { data } },
      })
    );
  }
);

export const updateOne = wrapQuery(
  db,
  (
    projectId: string,
    changes: Changes<ProjectEntity>
  ): PromiseExtended => {
    return db.projects.update(projectId, changes).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.updateOne, 'projects'),
        errorObj: e,
        additionalInfo: { query: { data: { projectId, changes } } },
      })
    );
  }
);

export const removeBatch = makeDefaultRemoveBatch<string, ProjectEntity>(
  db,
  db.projects,
  '_id'
);
